/**
 * MiniMax on fal.ai (Phase 1, U4): MiniMax H3 and the two Hailuo generations.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * These descriptors reproduce TODAY's behaviour byte for byte (P1 is price- and payload-neutral).
 * Every quirk that is not a real schema fact lives in `legacy.*` with a retirement note, so the
 * conformance debt report counts it. Replaces, once the family is flipped and cleaned (C1):
 *   - falai.ts video route: the H3 pre-reserve resolution 400, the H3 FLF endpoint override, the
 *     'minimax-hailuo' / 'hailuo-2.3' and 'minimax-h3' switch arms;
 *   - ProviderService.generateVideoFalAI: the generic headless shape (Hailuo) and the H3 branch;
 *   - CreditService.calculateCost MINIMAX_H3_RATES; the generic ladder for the Hailuo rows.
 *
 * Schemas: server/config/models/__schemas__ (scaffold-fal, fal OpenAPI oracle, 2026-09-19).
 * H3 is on the BARE `minimax/` namespace; both Hailuo generations are under `fal-ai/minimax/`.
 */
import type { LegacyWire, ModelDescriptor, Param } from '../types';
import { END, IMG, W_END, W_IMG } from '../_shared';

const ORACLE_DATE = '2026-09-19';

// ─── MiniMax H3 ────────────────────────────────────────────────────────────────────────────
// Plan §2 example 2, checked against the U0 goldens. Since d01af92 legacy reserves the length it
// RENDERS (minimaxH3Seconds, 5..15), and the legacy mappers hand the engine that same value.
// units.stage is 'resolved' (P1-WMLP): the clamp coercion IS minimaxH3Seconds (round, 5..15), so
// a direct engine call bills the length it wires too (30 s bills and renders 15 s), not the raw
// request the pre-hotfix route billed.
export const MINIMAX_H3: ModelDescriptor = {
  id: 'minimax-h3', provider: 'falai', face: 'video', displayName: 'MiniMax H3', ledgerName: 'MiniMax H3', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'minimax/h3/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'minimax/h3/image-to-video', ports: [IMG()], media: { image: W_IMG } },
    // FLF reuses the image-to-video endpoint with end_image_url (legacyEditorMode: isI2vEndpointFlfModel).
    flf: { endpoint: 'minimax/h3/image-to-video', ports: [IMG('input-image-first'), END()], media: { image: W_IMG, endImage: W_END } },
  },
  params: [
    { id: 'resolution', type: 'enum', values: ['480P', '768P', '2K', '4K'], default: '2K', normalize: 'upper', role: 'tier',
      // Editor: 400 before reserving (falai.ts, canonicalMinimaxH3Resolution). Headless: an
      // unrecognised value renders at fal's 2K default and CreditService prices it at 2K (R10).
      invalid: { editor: 'reject', headless: { coerce: { kind: 'default' } } },
      wire: { field: 'resolution' },
      ui: { label: 'Resolution', tier: 'basic', optionLabels: { '480P': '480P (native)', '768P': '768P (native)',
                                                                 '2K': '2K (768P upscaled)', '4K': '4K (768P upscaled)' } } },
    { id: 'duration', type: 'int', min: 5, max: 15, uiValues: [5, 6, 8, 10, 12, 15], default: 5, role: 'units',
      // Math.min(15, Math.max(5, Math.round(Number(duration) || 5))) on both surfaces.
      invalid: { coerce: { kind: 'clamp', min: 5, max: 15 } }, wire: { field: 'duration', as: 'integer' },
      ui: { label: 'Duration', tier: 'basic' } },
    { id: 'aspectRatio', type: 'enum', values: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'], default: '16:9', onlyModes: ['t2v'],
      // Editor sends sanitizeAspectRatio(raw, '16:9'); headless sends `aspectRatio || '16:9'` verbatim.
      // Only when the route's isTextToVideo holds ($t2v): false on its t2v-endpoint fallbacks.
      sendWhen: { $t2v: { eq: true } },
      invalid: 'passthrough', wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' } },
  ],
  pricing: {
    // d01af92: reserve bills the clamped length; 'resolved' is that length (see the header note).
    unit: 'second', units: { param: 'duration', stage: 'resolved' }, rounding: 'table-min1', policy: 'fal-video-35',
    skus: [{
      id: 'minimax-h3', rate: { by: ['resolution'], table: { '480P': 24, '768P': 28, '2K': 60, '4K': 74 }, fallbackKey: '2K' },
      vendor: {
        usd: { '480P': 0.05, '768P': 0.06, '2K': 0.13, '4K': 0.16 }, per: 'second', field: 'pricingInfoOverride',
        source: 'fal.ai/models/minimax/h3/image-to-video + /text-to-video (migration 127)',
        text: 'Video costs **$0.05** per second at **480p**, **$0.06** per second at **768p**, **$0.13** per second at **2K** and **$0.16** per second at **4K**.',
        verified: ORACLE_DATE,
      },
      registry: { displayName: 'MiniMax H3', operationType: 'video', publishedTier: '2K', flatRate: true },
    }],
  },
  legacy: {
    notes: [
      'Editor FLF with ONE image, and i2v with lastImageUrl, go to the t2v endpoint WITHOUT aspect_ratio (legacy isTextToVideo is false there); the engine sees mode t2v and emits it. Needs the legacy t2v flag from the U2 mapper (reported, not reproducible from a descriptor).',
      'enable_safety_checker (fal default true), seed, prompt_expansion_mode and target_audio_url are never sent.',
    ],
  },
  knowledge: { summary: 'Audio is native and always on. 480P/768P native; 2K/4K upscale a 768P base. target_audio_url -> P2 lipsync.' },
  schemaVerified: ORACLE_DATE,
};

// ─── Hailuo (hailuo-02 standard, hailuo-2.3 standard) ──────────────────────────────────────
// Both are hidden in the editor (MinimaxVideo node) and billed through CreditService's GENERIC
// tail: ceil(credits_per_unit × ceil(units) × ladder(resolution)), flat_rate false.
//
// The legacy wire is almost entirely quirk, so it lives in legacy.wire:
//   editor   { prompt, duration: <body.duration verbatim>, image_url?, resolution: '1080p' ? '1080P' : '768P', prompt_optimizer: true }
//   headless { prompt, duration: Number(duration) || 8, aspect_ratio: aspectRatio || '16:9', image_url? }
// Oracle facts (2026-09-19) the legacy wire disagrees with — see decisionsForOwner in the U4 PR:
//   - `duration` is a STRING enum ["6","10"] on all four endpoints; the node offers 4 and 6 and
//     sends numbers;
//   - `resolution` exists ONLY on hailuo-02 image-to-video, and its enum is ["512P","768P"]:
//     '1080P' is out of enum there and a dead key everywhere else, yet 1080p bills 1.5x;
//   - `aspect_ratio` (headless) exists on none of them.

/** The route destructures `duration = 5` and forwards body.duration UNCHANGED (type included). */
const HAILUO_DURATION: Param = {
  // type 'string' + passthrough: a string is in domain and kept as is; a number is out of domain
  // and passes through untouched. Either way the wire carries the body value with its own JSON
  // type ('5' stays "5", 7.5 stays 7.5), exactly as the legacy arm does. P2 replaces this with
  // the schema's ["6","10"] enum (owner decision: the node offers 4/6).
  // default: the ROUTE default 5, not the node's '6' (R5 exception): the editor mapper always
  // supplies a value, so the default only answers priceSku(units undefined), which must bill the
  // route's 5 s (pricingParity at reserve level).
  // DIVERGENCE TO CARRY INTO P2 (decisionsForOwner): '5' is not a value this model offers ANYWHERE.
  // The node offers 4 and 6; the fal schema's enum is the STRING ["6","10"]. So the descriptor's
  // declared default agrees with neither, and P2 must not read it as "the intended node default" —
  // it exists only to reproduce what the route bills when body.duration is missing. The same '5'
  // is what the engine wires for a blank duration; on Hailuo that is out of enum and fal 422s
  // (minimax.parity.test.ts proves it), so it is not a free-generation vector here.
  id: 'duration', type: 'string', uiValues: [4, 6], default: '5', role: 'units', invalid: 'passthrough', wire: false,
  ui: { label: 'Duration', tier: 'basic' },
};

/**
 * Billing axis: the legacy ladder (getResolutionMultiplier) on the RAW string, so every value
 * passes through untouched. The wire value is legacy.wire below. Default = the node's '768p',
 * which the ladder bills exactly like the route's '720p' (both 1x).
 */
const HAILUO_RESOLUTION: Param = {
  id: 'resolution', type: 'string', uiValues: ['768p', '1080p'], default: '768p', role: 'tier', invalid: 'passthrough', wire: false,
  ui: { label: 'Resolution', tier: 'basic' },
};

/** Headless only: the generic builder's `aspect_ratio: aspectRatio || '16:9'`. */
const HAILUO_ASPECT: Param = {
  id: 'aspectRatio', type: 'string', default: '16:9', invalid: 'passthrough', wire: false,
  ui: { label: 'Aspect ratio', tier: 'hidden' },
};

const HAILUO_LEGACY_WIRE: readonly LegacyWire[] = [
  { field: 'duration', fromParam: 'duration',
    why: 'body.duration forwarded verbatim (number or string); schema types it as the STRING enum ["6","10"]', retire: 'owner-approval' },
  // resolution = body.resolution === '1080p' ? '1080P' : '768P' (case-sensitive), editor only.
  { field: 'resolution', value: '768P', surface: 'editor',
    why: 'editor arm default; absent from hailuo-2.3 and hailuo-02 t2v schemas, enum 512P|768P on hailuo-02 i2v', retire: 'owner-approval' },
  { field: 'resolution', fromParam: 'resolution', map: { '1080p': '1080P' }, surface: 'editor',
    why: "editor arm: only the exact string '1080p' maps to '1080P' (out of every Hailuo schema's enum)", retire: 'owner-approval' },
  { field: 'prompt_optimizer', value: true, surface: 'editor',
    why: 'editor-only constant; equals the fal default (headless never sends it)', retire: 'inert-cleanup' },
  { field: 'aspect_ratio', fromParam: 'aspectRatio', surface: 'headless',
    why: "headless generic builder `aspectRatio || '16:9'`; absent from every Hailuo schema", retire: 'inert-cleanup' },
];

const hailuo = (o: {
  id: 'hailuo-2.3' | 'minimax-hailuo'; name: string; slug: string; base: number; usd: number; text: string;
}): ModelDescriptor => ({
  id: o.id, provider: 'falai', face: 'video', displayName: o.name, ledgerName: o.name, status: 'hidden',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: `${o.slug}/text-to-video`, ports: [], media: {} },
    i2v: { endpoint: `${o.slug}/image-to-video`, ports: [IMG()], media: { image: W_IMG } },
  },
  params: [HAILUO_DURATION, HAILUO_RESOLUTION, HAILUO_ASPECT],
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
    skus: [{
      id: o.id, rate: { ladder: { base: o.base, by: 'resolution', curve: 'legacy-resolution-v1' } },
      vendor: { usd: { '720p': o.usd }, per: 'second', field: 'pricingInfoOverride', source: `fal.ai/models/${o.slug}/text-to-video`,
                text: o.text, verified: ORACLE_DATE },
      registry: { displayName: o.name, operationType: 'video', publishedTier: '720p', flatRate: false },
    }],
  },
  legacy: {
    wire: HAILUO_LEGACY_WIRE,
    notes: [
      'Editor first-last-frame with two images is refused 400 before reserving (falai.ts, 2860433: the VEO-shaped branch that posted it to the T2V endpoint was retired with the fal.ai VEO 3.1 model); the engine refuses it in resolve() (no flf mode, UNSUPPORTED_MODE).',
      "Editor duration '' / null reach fal as \"\" / null (legacy forwards them); the engine treats them as absent and sends the default.",
    ],
  },
  schemaVerified: ORACLE_DATE,
});

export const HAILUO_23 = hailuo({
  id: 'hailuo-2.3', name: 'MiniMax Hailuo 2.3', slug: 'fal-ai/minimax/hailuo-2.3/standard', base: 22,
  usd: 0.0467,   // $0.28 per 6 s video (the node's longest option); $0.56 per 10 s = $0.056/s
  text: 'Your request will cost **$0.28** per **6 second** video generation, and a **$0.56** per **10 second** video generation.',
});

export const MINIMAX_HAILUO = hailuo({
  id: 'minimax-hailuo', name: 'MiniMax Hailuo', slug: 'fal-ai/minimax/hailuo-02/standard', base: 21, usd: 0.045,
  text: 'For each second of video generated, it will cost **$0.045/sec**. A single 6 second video will cost **$0.27/video**.',
});

export const MINIMAX_MODELS: readonly ModelDescriptor[] = [MINIMAX_H3, HAILUO_23, MINIMAX_HAILUO];
